import theme from '@jetshop/ui/utils/theme';
import React, { Fragment } from 'react';
import { Query } from 'react-apollo';
import styled from 'react-emotion';

import CategoryLink from '@jetshop/ui/CategoryLink';
import { useLocation } from 'react-router';
import { useChannel } from '../../hooks/useChannel';
import GetCategories from './GetCategories.gql';
import SubCategoriesQuery from './SubCategories.gql';

import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDown,
  faAngleRight,
  faSearch,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
library.add(faAngleRight, faAngleDown, faSearch);

const CategoryList = styled('ul')`
  ${theme('above.md')} {
    width: 300px;
    padding-top: 45px;
  }
  padding: 10px;
  flex: 1 1 auto;
  flex-basis: 300px;
  flex-grow: 0;
  flex-shrink: 0;
  text-transform: uppercase;
  h2 {
    text-transform: uppercase;
    border-bottom: 1px solid #dedede;
    font-size: 18px;
    margin-bottom: 1em;
    padding-bottom: 0.5em;
    padding-left: 0;
  }
  input {
    border: 1px solid #dedede;
    padding: 10px;
    font-size: 16px;
    display: block;
    width: 100%;
    font-family: 'Abel', sans-serif;
    &:focus {
      outline: none;
      border-color: ${theme('colors.red')};
    }
  }

  a.active {
    &.skoterdelen {
      color: #cd3132;
      + svg {
        color: #cd3132;
        transform: rotate(90deg);
      }
    }
    &.eyewearstore {
      color: ${theme('colors.eyeWearYellow')};
      + svg {
        color: ${theme('colors.eyeWearYellow')};
        transform: rotate(90deg);
      }
    }
  }

  a,
  svg {
    &:hover {
      &.eyewearstore {
        color: ${theme('colors.eyeWearYellow')};
      }
      &.skoterdelen {
        color: ${theme('colors.red')};
      }
    }
  }

  li {
    font-size: 18px;
    margin-bottom: 0.5em;
    padding-left: 0;
    position: relative;
    li {
      padding-left: 0.5em;
    }
  }
  a {
    color: #000;
    text-decoration: none;
    display: block;
    border-bottom: 1px solid #f4f4f4;
    padding-bottom: 5px;
    transition: all 0.1s;
  }
`;

const SubCategoriesList = styled('ul')`
  margin-top: 0.5em;
  margin-left: 10px;
  li {
    font-size: 16px;
  }
`;

const NavIcon = styled(FontAwesomeIcon)`
  color: #6b6b6b;
  font-size: 1rem;
  transition: all 0.1s;
  position: absolute;
  right: 0;
  top: -1px;
`;

function SubCategories({ root, pathname, level = 1 }) {
  return (
    <SubCategoriesList>
      {root && (
        <Query query={SubCategoriesQuery} variables={{ root }}>
          {({ loading, error, data }) => {
            if (loading) return null;
            if (error) return `Error!: ${error}`;
            const categoriesOnLevel = data.categories.filter(
              (category) =>
                category.level === level || category.level === level + 1
            );
            return categoriesOnLevel.map((cat) => (
              <Fragment key={cat.id}>
                {cat.id !== root && (
                  <li key={cat.id}>
                    {cat.primaryRoute.id && (
                      <Fragment>
                        <CategoryLink category={cat}>{cat.name}</CategoryLink>
                        {cat.hasSubcategories && (
                          <NavIcon icon={['fal', 'angle-right']} />
                        )}
                        {pathname.startsWith(cat.primaryRoute.path) &&
                          cat.hasSubcategories && (
                            <SubCategories
                              root={cat.id}
                              pathname={pathname}
                              level={level + 1}
                            />
                          )}
                      </Fragment>
                    )}
                  </li>
                )}
              </Fragment>
            ));
          }}
        </Query>
      )}
    </SubCategoriesList>
  );
}

const everybodysFakeAnyway = Array(10);

function CategorySidebar() {
  const { pathname } = useLocation();
  const { isWeAhl } = useChannel();
  return (
    <CategoryList>
      <Query query={GetCategories}>
        {({ loading, error, data }) => {
          if (loading) {
            return (
              <ul style={{ width: '300px' }}>
                {everybodysFakeAnyway.map((_, l) => (
                  <li
                    style={{
                      borderBottom: '1px solid #f3f3f3',
                      minHeight: '23px',
                    }}
                    key={l}
                  />
                ))}
              </ul>
            );
          }
          if (error) return `Error!: ${error}`;
          return data.categories.map((cat) => (
            <li key={cat.id}>
              {cat.primaryRoute.path && (
                <>
                  <CategoryLink
                    category={cat}
                    data-testid="category-sidebar-link"
                    className={isWeAhl ? 'eyewearstore' : 'skoterdelen'}
                  >
                    {cat.name}
                  </CategoryLink>
                  {cat.hasSubcategories && (
                    <NavIcon icon={['fal', 'angle-right']} />
                  )}
                  {cat?.primaryRoute?.path &&
                    pathname.startsWith(cat.primaryRoute.path) && (
                      <SubCategories root={cat.id} pathname={pathname} />
                    )}
                </>
              )}
            </li>
          ));
        }}
      </Query>
    </CategoryList>
  );
}

export default CategorySidebar;
